import { AgendaSession, AgendaDay } from "@features/events/types";

/**
 * Converts a time string (HH:MM) to minutes since midnight
 * @param timeStr - Time string in format "HH:MM" (e.g., "09:30")
 * @returns Number of minutes since midnight
 */
export const timeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Checks if two sessions have overlapping time ranges
 * @param session1 - First session to compare
 * @param session2 - Second session to compare
 * @returns true if sessions overlap, false otherwise
 */
export const sessionsOverlap = (
  session1: AgendaSession,
  session2: AgendaSession
): boolean => {
  if (!session1.from || !session1.to || !session2.from || !session2.to) {
    return false;
  }

  const session1Start = timeToMinutes(session1.from);
  const session1End = timeToMinutes(session1.to);
  const session2Start = timeToMinutes(session2.from);
  const session2End = timeToMinutes(session2.to);

  return session1Start < session2End && session2Start < session1End;
};

/**
 * Sorts an array of sessions by their start time in ascending order
 * @param sessions - Array of sessions to sort
 * @returns New array of sessions sorted by start time
 */
export const sortSessionsByTime = (sessions: AgendaSession[]): AgendaSession[] => {
  return [...sessions].sort((a, b) => {
    if (!a.from || !b.from) return 0;
    return timeToMinutes(a.from) - timeToMinutes(b.from);
  });
};

/**
 * Normalizes agenda data by ensuring all sessions are sorted by time
 * @param agenda - Array of agenda days to normalize
 * @returns New array with all sessions sorted by time within each swimlane
 */
export const normalizeAgendaData = (agenda: AgendaDay[]): AgendaDay[] => {
  return agenda.map((day) => ({
    ...day,
    swimlines: day.swimlines?.map((swimlane) => ({
      ...swimlane,
      sessions: sortSessionsByTime(swimlane.sessions || []),
    })) || [],
  }));
};

